// src/components/PatientDetails/panels/ActionPanel.tsx
// -----------------------------------------------------------------------------
// ActionPanel - Right sidebar with patient action buttons
// -----------------------------------------------------------------------------
// Features:
// - Icon-only buttons with tooltips
// - Delete, Share, Export, Copy Link actions
// - Vertical stack layout
// - Confirmation dialogs for destructive actions
// -----------------------------------------------------------------------------

import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  alpha,
  useTheme,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Share as ShareIcon,
  FileDownload as ExportIcon,
  Link as LinkIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import React, { useState, useCallback } from "react";
import { useNotification } from "hooks/useNotification";
import { useTranslation } from "../../../i18n";
import {
  athenaBackdropSx,
  athenaDarkActionsSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkTitleSx,
} from "../../layout/AthenaDialogStyles";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ActionPanelProps {
  /** Patient ID */
  patientId?: string | number;
  /** Patient name (for confirmation dialogs) */
  patientName?: string;
  /** Handler for delete action */
  onDelete?: () => Promise<void>;
  /** Handler for share action */
  onShare?: () => void;
  /** Handler for export action */
  onExport?: () => Promise<void>;
  /** Disable all actions */
  disabled?: boolean;
  /** Handler for close button (mobile drawer) */
  onClose?: () => void;
  /** Show close button (for drawer mode) */
  showClose?: boolean;
  /** Layout: "vertical" (default right column) or "inline" (horizontal row in header) */
  layout?: "vertical" | "inline";
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ActionPanel: React.FC<ActionPanelProps> = ({
  patientId,
  patientName,
  onDelete,
  onShare,
  onExport,
  disabled = false,
  onClose,
  showClose = false,
  layout = "vertical",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const notification = useNotification();

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Copy patient URL to clipboard
  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/patients/${patientId}/details`;
    try {
      await navigator.clipboard.writeText(url);
      notification.success(t("action_panel.link_copied", "Patient link copied to clipboard"));
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      notification.success(t("action_panel.link_copied", "Patient link copied to clipboard"));
    }
  }, [patientId, notification]);

  // Handle delete with confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setDeleteDialogOpen(false);
      notification.success(t("action_panel.patient_deleted", "Patient deleted successfully"));
    } catch (err) {
      notification.error(t("patient.delete_failed", "Failed to delete patient"));
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, notification]);

  // Handle export
  const handleExport = useCallback(async () => {
    if (!onExport) return;
    setIsExporting(true);
    try {
      await onExport();
      notification.success(t("action_panel.export_completed", "Export completed"));
    } catch (err) {
      notification.error(t("action_panel.export_failed", "Export failed"));
    } finally {
      setIsExporting(false);
    }
  }, [onExport, notification]);

  const isInline = layout === "inline";
  const btnSize = isInline ? 30 : 44;
  const iconSize = isInline ? 16 : 20;

  const buttonSx = {
    width: btnSize,
    height: btnSize,
    borderRadius: isInline ? "50%" : 2,
    backgroundColor: isInline
      ? alpha(theme.palette.primary.dark, 0.15)
      : alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.medium || 0.75),
    backdropFilter: "blur(8px)",
    border: `1px solid ${isInline ? alpha(theme.palette.common.white, 0.18) : alpha(theme.palette.divider, 0.3)}`,
    color: isInline
      ? alpha(theme.palette.common.white, 0.65)
      : (theme.custom?.contentText?.secondary || theme.palette.text.secondary),
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isInline
        ? alpha(theme.palette.primary.main, 0.25)
        : alpha(theme.palette.primary.main, 0.1),
      borderColor: isInline
        ? alpha(theme.palette.common.white, 0.35)
        : alpha(theme.palette.primary.main, 0.3),
      color: isInline ? theme.palette.common.white : theme.palette.primary.main,
      transform: "scale(1.05)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "&.Mui-disabled": {
      backgroundColor: alpha(theme.palette.background.paper, 0.3),
      color: alpha(theme.palette.text.secondary, 0.3),
    },
  };

  const dangerButtonSx = {
    ...buttonSx,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, isInline ? 0.25 : 0.1),
      borderColor: alpha(theme.palette.error.main, 0.3),
      color: theme.palette.error.main,
      transform: "scale(1.05)",
    },
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isInline ? "row" : "column",
          alignItems: "center",
          gap: isInline ? 0.5 : 1.5,
          py: isInline ? 0 : 2,
          px: isInline ? 0 : 1,
          height: isInline ? "auto" : "100%",
        }}
      >
        {/* Primary Actions */}
        <Tooltip title={t("actions.copy_link", "Copy Patient Link")} placement={isInline ? "top" : "left"}>
          <span>
            <IconButton
              onClick={handleCopyLink}
              disabled={disabled || !patientId}
              sx={buttonSx}
            >
              <LinkIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("actions.share", "Share Patient")} placement={isInline ? "top" : "left"}>
          <span>
            <IconButton
              onClick={onShare}
              disabled={disabled || !onShare}
              sx={buttonSx}
            >
              <ShareIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </span>
        </Tooltip>

        {!isInline && (
          <Divider
            sx={{
              width: 32,
              borderColor: alpha(theme.palette.divider, 0.3),
              my: 0.5,
            }}
          />
        )}

        <Tooltip title={t("action_panel.export_data", "Export Data")} placement={isInline ? "top" : "left"}>
          <span>
            <IconButton
              onClick={handleExport}
              disabled={disabled || !onExport || isExporting}
              sx={buttonSx}
            >
              {isExporting ? (
                <CircularProgress size={iconSize - 2} />
              ) : (
                <ExportIcon sx={{ fontSize: iconSize }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {!isInline && (
          <Divider
            sx={{
              width: 32,
              borderColor: alpha(theme.palette.divider, 0.3),
              my: 0.5,
            }}
          />
        )}

        {/* Destructive Action */}
        <Tooltip title={t("actions.delete_patient", "Delete Patient")} placement={isInline ? "top" : "left"}>
          <span>
            <IconButton
              onClick={() => setDeleteDialogOpen(true)}
              disabled={disabled || !onDelete}
              sx={dangerButtonSx}
            >
              <DeleteIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* Spacer (vertical mode only) */}
        {!isInline && <Box sx={{ flex: 1 }} />}

        {/* Close Button at bottom (where FAB was clicked) - drawer mode only */}
        {showClose && onClose && (
          <Tooltip title={t("actions.close_menu", "Close Menu")} placement={isInline ? "top" : "left"}>
            <IconButton
              onClick={onClose}
              sx={{
                ...buttonSx,
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.25),
                  transform: "scale(1.05)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Delete Confirmation Dialog — dark variant (cursor_logs/069). Was the
          LIGHT variant, which put a periwinkle body between a navy title and a
          navy action bar: three surfaces, two of them dark. The destructive
          action keeps its error colour to signal intent. */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          <DeleteIcon fontSize="small" />
          {t("patient.delete_title", "Delete Patient?")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <DialogContentText>
            {t("patient.delete_confirm", "Are you sure you want to delete {{name}}? This will also delete all related tables and data. This action cannot be undone.").replace("{{name}}", patientName || `Patient #${patientId}`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={athenaDarkActionsSx}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
            sx={athenaDarkSecondaryButtonSx}
          >
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              // error.main is tuned for a white card; on navy it needs the
              // light family (cursor_logs/069 §2.1).
              backgroundColor: theme.palette.error.light,
              color: theme.palette.common.white,
              "&:hover": { backgroundColor: theme.palette.error.main },
            }}
          >
            {isDeleting ? t("action_panel.deleting", "Deleting...") : t("action_panel.delete_title", "Delete Patient")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionPanel;
